import { Conversation, Message, MessageRole, Trace } from '../models/copilot.models';
import {
  ConversationalBtmConversationDetail,
  ConversationalBtmMessage,
  ConversationalBtmMessagePart,
} from './conversational-btm.models';
import {
  ConversationalBtmRunState,
  conversationalBtmRunMessages,
} from './run-message-reducer';

export function mapConversationalBtmMessage(
  message: ConversationalBtmMessage,
  conversationId: string
): Message {
  const parts = message.parts ?? [];
  return {
    id: message.id,
    conversation_id: conversationId,
    content: message.content ?? textContent(parts),
    role: mapRole(message.role),
    created_at: message.created_at ?? '',
    traces: reasoningTraces(parts),
    metadata: {
      ...(message.metadata ?? {}),
      run_id: message.run_id,
      parts,
    },
  };
}

export function mapConversationalBtmDetail(
  detail: ConversationalBtmConversationDetail
): Conversation {
  const conversation = detail.conversation;
  return {
    id: conversation.id,
    title: conversation.title,
    created_at: conversation.created_at,
    updated_at: conversation.last_activity,
    owner_id: conversation.owner_id,
    metadata: {
      ...conversation.metadata,
      execution: conversation.execution,
      next_cursor: detail.next_cursor,
      has_more: detail.has_more,
    },
    messages: detail.messages.map((message) =>
      mapConversationalBtmMessage(message, conversation.id)
    ),
  };
}

export function mapConversationalBtmRunState(
  state: ConversationalBtmRunState,
  conversationId: string,
  createdAt: string
): Message | undefined {
  const segments = conversationalBtmRunMessages(state);
  if (segments.length === 0) return undefined;

  const parts = segments.flatMap((segment) => segment.parts);
  const content = segments
    .map((segment) => segment.content)
    .filter(Boolean)
    .join('\n\n');
  const displayContent = content || textContent(parts);
  if (!displayContent.trim()) return undefined;

  return {
    id: `${state.runId}:assistant`,
    conversation_id: conversationId,
    content: displayContent,
    role: MessageRole.ASSISTANT,
    created_at: createdAt,
    traces: reasoningTraces(parts),
    metadata: {
      run_id: state.runId,
      parts,
      segment_message_ids: segments.map((segment) => segment.id),
      streaming: true,
    },
  };
}

function textContent(parts: readonly ConversationalBtmMessagePart[]): string {
  return parts
    .filter((part) => part.type === 'text' && typeof part['text'] === 'string')
    .map((part) => part['text'] as string)
    .join('');
}

function reasoningTraces(
  parts: readonly ConversationalBtmMessagePart[]
): Trace[] | undefined {
  const traces = parts.flatMap((part, index) =>
    part.type === 'reasoning' && typeof part['text'] === 'string'
      ? [{ index, type: 'reasoning', reasoning: part['text'] as string }]
      : []
  );
  return traces.length > 0 ? traces : undefined;
}

function mapRole(role: string): MessageRole {
  if (role === MessageRole.USER) return MessageRole.USER;
  if (role === MessageRole.ASSISTANT) return MessageRole.ASSISTANT;
  return MessageRole.SYSTEM;
}
